import { SxProps, Theme } from "@mui/material";
import myPlanTokens, { myPlanResponsiveTokens } from "./MyPlan.tokens";

const t = myPlanTokens;
const r = myPlanResponsiveTokens;

/** Card gutter, per breakpoint (`md` restates the desktop value). */
const gutter = { xs: r.gutter, md: t.space.gutter };

/** White rounded card chrome shared by all three Plan & Billing sections. */
export const planCardSx: SxProps<Theme> = {
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: t.radius.card,
  boxShadow: t.shadow.card,
  overflow: "hidden",
};

/** Plan name in the summary card header. */
export const planNameSx: SxProps<Theme> = {
  // Rendered as a heading element; reset the browser's default heading margin.
  m: 0,
  fontSize: { xs: r.planNameFontSize, md: t.font.planName },
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: "normal",
};

/** Card header title ("Payment Methods", "Invoices"). */
export const sectionTitleSx: SxProps<Theme> = {
  // Rendered as a heading element; reset the browser's default heading margin.
  m: 0,
  fontSize: { xs: r.sectionTitleFontSize, md: t.font.sectionTitle },
  fontWeight: t.weight.heading,
  color: t.color.ink,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: "normal",
};

/** Card header supporting line, also used for the plan sub-title. */
export const sectionDescSx: SxProps<Theme> = {
  fontSize: { xs: r.descFontSize, md: t.font.sectionDesc },
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: "normal",
};

/**
 * Header block above a list. `stacked` renders the title/description column
 * on its own (Invoices); otherwise the header hosts a trailing action.
 */
export const sectionHeaderSx = (stacked: boolean): SxProps<Theme> => ({
  px: gutter,
  pt: { xs: r.headerY, md: t.space.sectionHeaderTop },
  pb: { xs: r.headerY, md: t.space.sectionHeaderBottom },
  display: "flex",
  // Below `md` the reference stacks the title, the sentence and the action, and
  // runs the action full width under them.
  flexDirection: { xs: "column", md: stacked ? "column" : "row" },
  alignItems: { xs: "stretch", md: stacked ? "flex-start" : "center" },
  gap: {
    xs: stacked ? r.sectionHeaderStackedGap : r.sectionHeaderGap,
    md: stacked ? "2px" : "16px",
  },
  borderBottom: {
    xs: `${t.border.hairline} solid ${t.color.rowDivider}`,
    md: "none",
  },
});

/** Grey column-header strip. */
export const columnHeaderSx = (columns: string): SxProps<Theme> => ({
  // Neither reference draws a column strip — below `md` the rows stop being a
  // grid, so the strip has nothing left to label.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: columns,
  px: t.space.gutter,
  py: t.space.columnHeaderY,
  bgcolor: t.color.headerBg,
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
  borderBottom: `${t.border.hairline} solid ${t.color.headerBorder}`,
  fontSize: t.font.columnHeader,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.columnHeader,
  color: t.color.muted,
  lineHeight: "normal",
});

/** A single list row; the last row drops its divider. */
export const listRowSx = (columns: string, isLast: boolean): SxProps<Theme> => ({
  display: { xs: "flex", md: "grid" },
  gridTemplateColumns: { md: columns },
  alignItems: "center",
  gap: { xs: r.row.gap, md: 0 },
  px: gutter,
  py: { xs: "13px", md: t.space.rowY },
  borderBottom: isLast
    ? "none"
    : `${t.border.hairline} solid ${t.color.rowDivider}`,
});

/** Placeholder shown in place of rows when a list is empty. */
export const emptyStateSx: SxProps<Theme> = {
  px: gutter,
  py: "34px",
  textAlign: "center",
  fontSize: t.font.sectionDesc,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: "normal",
};

/** Filled primary button (Change plan / Add Payment Method). */
export const accentButtonSx = (
  height: string,
  paddingX: string,
): SxProps<Theme> => ({
  // Both small references run the primary action full width at 44px.
  width: { xs: "100%", md: "auto" },
  height: { xs: r.buttonHeight, md: height },
  minHeight: { xs: r.buttonHeight, md: height },
  px: { xs: r.buttonPaddingX, md: paddingX },
  fontSize: { xs: r.buttonFontSize, md: t.font.button },
  py: 0,
  bgcolor: t.color.ctaBg,
  color: t.color.onAccent,
  border: "none",
  borderRadius: t.radius.control,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: t.color.ctaBgHover, boxShadow: "none" },
  "&.Mui-disabled": {
    bgcolor: t.color.ctaBg,
    opacity: 0.5,
    color: t.color.onAccent,
  },
});

/** Outlined secondary button (Cancel subscription). */
export const ghostButtonSx: SxProps<Theme> = {
  height: { xs: r.buttonHeight, md: t.size.buttonHeight },
  minHeight: { xs: r.buttonHeight, md: t.size.buttonHeight },
  px: { xs: r.ghostPaddingX, md: "16px" },
  fontSize: { xs: r.ghostFontSize, md: t.font.button },
  py: 0,
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  borderRadius: t.radius.control,
  fontWeight: t.weight.strong,
  color: t.color.body,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: t.color.surface,
    color: t.color.ink,
    borderColor: t.color.ink,
    boxShadow: "none",
  },
};

interface PillOptions {
  color: string;
  bgcolor: string;
  fontSize: string;
  letterSpacing?: string;
  paddingY?: string;
  uppercase?: boolean;
}

/** Small rounded status pill (PAID / EXPIRED / MONTHLY). */
export const pillSx = ({
  color,
  bgcolor,
  fontSize,
  letterSpacing,
  paddingY = "2px",
  uppercase = false,
}: PillOptions): SxProps<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  fontSize,
  fontWeight: t.weight.strong,
  letterSpacing: letterSpacing ?? "normal",
  color,
  bgcolor,
  px: "9px",
  py: paddingY,
  borderRadius: t.radius.pill,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  textTransform: uppercase ? "uppercase" : "none",
});

/** Card-brand plate ("DISC", "UNION", "MC"). */
export const brandPlateSx = (color: string): SxProps<Theme> => ({
  width: t.size.plateWidth,
  height: t.size.plateHeight,
  flexShrink: 0,
  borderRadius: t.radius.plate,
  bgcolor: t.color.brandPlateBg,
  border: `${t.border.hairline} solid ${t.color.brandPlateBorder}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: t.font.brandPlate,
  fontWeight: t.weight.heading,
  lineHeight: "normal",
  color,
});

/** Stat-strip cell label ("PRICE", "SEATS", …). */
export const statLabelSx: SxProps<Theme> = {
  fontSize: { xs: r.stat.labelFontSize, md: t.font.columnHeader },
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.statLabel,
  color: t.color.muted,
  lineHeight: "normal",
};

/** Stat-strip headline value. */
export const statValueSx = (color: string): SxProps<Theme> => ({
  fontSize: { xs: r.stat.valueFontSize, md: t.font.statValue },
  fontWeight: t.weight.heading,
  color,
  letterSpacing: t.letterSpacing.heading,
  lineHeight: "normal",
});

/** Trailing unit inside a stat value ("/mo", " of 10 used"). */
export const statUnitSx: SxProps<Theme> = {
  fontSize: { xs: r.stat.unitFontSize, md: t.font.statUnit },
  fontWeight: t.weight.medium,
  color: t.color.muted,
  letterSpacing: "normal",
};

/** Stat-strip caption under the value. */
export const statCaptionSx: SxProps<Theme> = {
  fontSize: t.font.statCaption,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: "normal",
};

/** A stat-strip cell; separators collapse to horizontal rules when stacked. */
/**
 * A stat-strip cell. Four across on desktop; both small references lay the same
 * four out as a 2 x 2 grid, so the separators become a cross rather than three
 * vertical rules.
 */
export const statCellSx = (index: number, total: number): SxProps<Theme> => {
  const rule = `${t.border.hairline} solid ${t.color.rowDivider}`;
  return {
    px: gutter,
    py: { xs: "14px", md: t.space.statCellY },
    display: "flex",
    flexDirection: "column",
    gap: { xs: r.stat.gap, md: "5px" },
    minWidth: 0,
    // Four across on desktop, 2 x 2 below it: the vertical rule follows the
    // left cell of each pair, the horizontal one closes the first row.
    borderRight: {
      xs: index % 2 === 0 ? rule : "none",
      md: index === total - 1 ? "none" : rule,
    },
    borderBottom: { xs: index < 2 ? rule : "none", md: "none" },
  };
};

/** Body text inside a list row. */
export const rowTextSx = (
  color: string,
  fontWeight: number,
): SxProps<Theme> => ({
  fontSize: t.font.rowText,
  fontWeight,
  color,
  lineHeight: "normal",
});

/** Pagination footer strip under the invoice list. */
export const paginationBarSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  px: gutter,
  py: t.space.footerY,
  borderTop: `${t.border.hairline} solid ${t.color.headerBorder}`,
};

/** MUI <Pagination> restyled to the design's 28px square items. */
export const paginationSx: SxProps<Theme> = {
  "& .MuiPagination-ul": { gap: "4px", flexWrap: "nowrap" },
  "& .MuiPagination-ul > li": { m: 0 },
  "& .MuiPaginationItem-root": {
    minWidth: t.size.pageItem,
    width: t.size.pageItem,
    height: t.size.pageItem,
    m: 0,
    p: 0,
    borderRadius: t.radius.pageItem,
    bgcolor: "transparent",
    fontSize: t.font.pageItem,
    fontWeight: t.weight.medium,
    color: t.color.body,
    "&:hover": { bgcolor: t.color.hoverFill },
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    bgcolor: t.color.ctaBg,
    color: t.color.onAccent,
    fontWeight: t.weight.strong,
    "&:hover": { bgcolor: t.color.ctaBg, color: t.color.onAccent },
  },
  "& .MuiPaginationItem-ellipsis": {
    width: "auto",
    minWidth: "unset",
    px: "4px",
    height: t.size.pageItem,
    display: "flex",
    alignItems: "center",
    color: t.color.muted,
    bgcolor: "transparent",
    fontSize: t.font.pageItem,
    fontWeight: t.weight.regular,
  },
  "& .MuiPaginationItem-icon": {
    width: t.size.pageArrow,
    height: t.size.pageArrow,
    fontSize: t.size.pageArrow,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    color: t.color.disabledControl,
    opacity: 1,
  },
};

/** "No Active Subscription" empty state inside the plan summary card. */
export const noPlanTitleSx: SxProps<Theme> = {
  // Rendered as a heading element; reset the browser's default heading margin.
  m: 0,
  fontSize: t.font.planName,
  fontWeight: t.weight.heading,
  letterSpacing: t.letterSpacing.heading,
  color: t.color.ink,
  lineHeight: "normal",
};

export const noPlanBodySx: SxProps<Theme> = {
  fontSize: t.font.sectionDesc,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: 1.6,
};

/* ------------------------------------------------- small-viewport list rows */

/** Brand plate + card number, side by side inside the payment row. */
export const paymentCardCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: r.row.gap, md: "12px" },
  minWidth: 0,
  flex: { xs: 1, md: "none" },
};

/**
 * Card number and its EXPIRED badge. The reference stacks them below `md`,
 * where there is no room for the badge beside a masked number.
 */
export const paymentCardTextSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: { xs: "flex-start", md: "center" },
  gap: { xs: r.row.textGap, md: "12px" },
  minWidth: 0,
};

/**
 * "Change plan" shares its row with "Cancel subscription" below `md`, so it
 * flexes into the space left over instead of taking the full width.
 *
 * Composed here rather than as an `sx` array at the call site: spreading two
 * `SxProps<Theme>` values into MUI's `sx` prop does not type-check (TS2769).
 */
export const planActionPrimarySx = (
  height: string,
  paddingX: string,
): SxProps<Theme> => ({
  ...(accentButtonSx(height, paddingX) as object),
  width: "auto",
  flex: { xs: 1, md: "none" },
  minWidth: 0,
});

/** Wrapper around the header's trailing action. */
export const sectionHeaderActionSx: SxProps<Theme> = {
  ml: { md: "auto" },
  width: { xs: "100%", md: "auto" },
  flexShrink: 0,
};

/** Trash control, trailing the payment row. */
export const paymentRowActionSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  flexShrink: 0,
};

/** One invoice, drawn as a row of text plus a bordered download button. */
export const invoiceCardRowSx = (isLast: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: r.invoice.gap,
  px: r.gutter,
  py: "14px",
  borderBottom: isLast
    ? "none"
    : `${t.border.hairline} solid ${t.color.rowDivider}`,
});

export const invoiceCardTextSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: r.invoice.textGap,
};

export const invoiceCardTopSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
};

export const invoiceCardIdSx: SxProps<Theme> = {
  fontSize: r.invoice.idFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

export const invoiceCardMetaSx: SxProps<Theme> = {
  fontSize: r.invoice.subFontSize,
  color: t.color.body,
  lineHeight: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const invoiceDownloadButtonSx: SxProps<Theme> = {
  width: r.invoice.buttonSize,
  height: r.invoice.buttonSize,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: r.invoice.buttonRadius,
  border: `${t.border.hairline} solid ${t.color.controlBorder}`,
  color: t.color.accent,
  "&:hover": { color: t.color.accentHover, borderColor: t.color.accent },
};

/** Second line in the CHARGED cell: the gross and the credit behind it. */
export const creditCellSx: SxProps<Theme> = {
  display: "block",
  mt: "2px",
  fontSize: t.font.chip,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: "normal",
};

/**
 * Account-credit strip under the plan header. A downgrade leaves its money on
 * the account instead of the card, so this is the only place the customer can
 * see it — and the reason a later change can cost nothing.
 */
export const creditBannerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  gap: "8px",
  mx: t.space.gutter,
  my: "14px",
  px: "16px",
  py: "12px",
  borderRadius: t.radius.control,
  bgcolor: t.color.successBg,
};

export const creditAmountSx: SxProps<Theme> = {
  fontSize: t.font.planName,
  fontWeight: t.weight.heading,
  color: t.color.successText,
  lineHeight: "normal",
};

export const creditNoteSx: SxProps<Theme> = {
  fontSize: t.font.sectionDesc,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: "normal",
};

/**
 * Cancellation strip under the plan header — same shape as the credit banner,
 * `dangerBg`/`dangerText` in place of the success tone since this is telling
 * the customer their paid plan ended, even though they still have access.
 */
export const cancelledBannerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  flexWrap: "wrap",
  gap: "8px",
  mx: t.space.gutter,
  my: "14px",
  px: "16px",
  py: "12px",
  borderRadius: t.radius.control,
  bgcolor: t.color.dangerBg,
};

export const cancelledTitleSx: SxProps<Theme> = {
  fontSize: t.font.planName,
  fontWeight: t.weight.heading,
  color: t.color.dangerText,
  lineHeight: "normal",
};

export const cancelledNoteSx: SxProps<Theme> = {
  fontSize: t.font.sectionDesc,
  fontWeight: t.weight.regular,
  color: t.color.body,
  lineHeight: "normal",
};
